import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Building2, PlusCircle, Loader2, Download, UserCheck } from "lucide-react";

const EMPTY = {
  first_name: "", last_name: "", business_name: "", phone: "", email: "",
  address_line1: "", address_line2: "", city: "", state: "", postal_code: "", country: "",
  supervisor_available: false,
  supervisor: { first_name: "", last_name: "", email: "", phone: "" },
};

export default function Customers() {
  const { t } = useTranslation();
  const [companies, setCompanies] = useState(null);
  const [open, setOpen] = useState(false);
  const [zohoOpen, setZohoOpen] = useState(false);
  const [zohoList, setZohoList] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/companies").then((r) => setCompanies(r.data));
  useEffect(() => { load(); }, []);

  const openZoho = async () => {
    try {
      const { data } = await api.get("/companies/zoho-available");
      setZohoList(data.customers);
      setZohoOpen(true);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const importZoho = async (zid) => {
    try {
      await api.post("/companies/import-zoho", { zoho_customer_id: zid });
      toast.success(t("customers.importedToast"));
      setZohoList(zohoList.filter((c) => c.zoho_customer_id !== zid));
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const save = async () => {
    if (!form.business_name) return toast.error(t("customers.businessNameRequired"));
    setSaving(true);
    try {
      const payload = { ...form };
      if (!form.supervisor_available) payload.supervisor = null;
      const { data } = await api.post("/companies", payload);
      if (data.supervisor_credentials)
        toast.success(`${data.supervisor_credentials.email} / ${data.supervisor_credentials.password}`, { duration: 8000 });
      else toast.success(t("customers.createdSyncedToast"));
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setSup = (k, v) => setForm((f) => ({ ...f, supervisor: { ...f.supervisor, [k]: v } }));

  if (!companies)
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>;

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-3xl font-black text-slate-900">{t("customers.title")}</h1>
        <div className="flex gap-2">
          <Button data-testid="zoho-import-btn" onClick={openZoho} variant="outline" className="gap-2 border-slate-300"><Download className="h-4 w-4" /> {t("customers.importFromZoho")}</Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-company-btn" className="gap-2 bg-blue-600 hover:bg-blue-500"><PlusCircle className="h-4 w-4" /> {t("customers.newCustomer")}</Button>
            </DialogTrigger>
            <DialogContent className="max-h-[88vh] max-w-3xl overflow-y-auto border-slate-200 bg-white">
              <DialogHeader><DialogTitle className="font-heading text-slate-900">{t("customers.newCustomerTitle")}</DialogTitle><DialogDescription>{t("customers.newCustomerDesc")}</DialogDescription></DialogHeader>
              <div className="grid gap-5 md:grid-cols-2">
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="overline text-slate-500">{t("customers.firstName")}</Label><Input data-testid="cust-first" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} className="mt-1.5 border-slate-300 bg-white" /></div>
                    <div><Label className="overline text-slate-500">{t("customers.lastName")}</Label><Input data-testid="cust-last" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} className="mt-1.5 border-slate-300 bg-white" /></div>
                  </div>
                  <div><Label className="overline text-slate-500">{t("customers.businessName")}</Label><Input data-testid="cust-business" value={form.business_name} onChange={(e) => set("business_name", e.target.value)} className="mt-1.5 border-slate-300 bg-white" /></div>
                  <div><Label className="overline text-slate-500">{t("customers.phone")}</Label><Input data-testid="cust-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1.5 border-slate-300 bg-white" /></div>
                  <div><Label className="overline text-slate-500">{t("customers.email")}</Label><Input data-testid="cust-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5 border-slate-300 bg-white" /></div>
                  <div><Label className="overline text-slate-500">{t("customers.addressLine1")}</Label><Input value={form.address_line1} onChange={(e) => set("address_line1", e.target.value)} className="mt-1.5 border-slate-300 bg-white" /></div>
                  <div><Label className="overline text-slate-500">{t("customers.addressLine2")}</Label><Input value={form.address_line2} onChange={(e) => set("address_line2", e.target.value)} className="mt-1.5 border-slate-300 bg-white" /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="overline text-slate-500">{t("customers.city")}</Label><Input value={form.city} onChange={(e) => set("city", e.target.value)} className="mt-1.5 border-slate-300 bg-white" /></div>
                    <div><Label className="overline text-slate-500">{t("customers.state")}</Label><Input value={form.state} onChange={(e) => set("state", e.target.value)} className="mt-1.5 border-slate-300 bg-white" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="overline text-slate-500">{t("customers.postalCode")}</Label><Input value={form.postal_code} onChange={(e) => set("postal_code", e.target.value)} className="mt-1.5 border-slate-300 bg-white" /></div>
                    <div><Label className="overline text-slate-500">{t("customers.country")}</Label><Input value={form.country} onChange={(e) => set("country", e.target.value)} className="mt-1.5 border-slate-300 bg-white" /></div>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 p-3">
                    <div><Label className="text-slate-900">{t("customers.supervisorAvailable")}</Label><p className="text-xs text-slate-500">{t("customers.supervisorAvailableDesc")}</p></div>
                    <Switch data-testid="cust-supervisor-toggle" checked={form.supervisor_available} onCheckedChange={(v) => set("supervisor_available", v)} />
                  </div>
                  {form.supervisor_available && (
                    <div className="space-y-3 rounded-md border border-blue-200 bg-blue-50/50 p-3">
                      <div className="grid grid-cols-2 gap-2">
                        <div><Label className="overline text-slate-500">{t("customers.supFirstName")}</Label><Input data-testid="sup-first" value={form.supervisor.first_name} onChange={(e) => setSup("first_name", e.target.value)} className="mt-1.5 border-slate-300 bg-white" /></div>
                        <div><Label className="overline text-slate-500">{t("customers.supLastName")}</Label><Input data-testid="sup-last" value={form.supervisor.last_name} onChange={(e) => setSup("last_name", e.target.value)} className="mt-1.5 border-slate-300 bg-white" /></div>
                      </div>
                      <div><Label className="overline text-slate-500">{t("customers.supervisorEmail")}</Label><Input data-testid="sup-email" type="email" value={form.supervisor.email} onChange={(e) => setSup("email", e.target.value)} className="mt-1.5 border-slate-300 bg-white" /></div>
                      <div><Label className="overline text-slate-500">{t("customers.supervisorPhone")}</Label><Input data-testid="sup-phone" value={form.supervisor.phone} onChange={(e) => setSup("phone", e.target.value)} className="mt-1.5 border-slate-300 bg-white" /></div>
                      <p className="text-xs text-slate-500">{t("customers.supervisorNote")}</p>
                    </div>
                  )}
                </div>
              </div>
              <DialogFooter>
                <Button data-testid="company-save" onClick={save} disabled={saving || !form.business_name} className="bg-blue-600 hover:bg-blue-500">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("customers.createCustomer")}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {companies.map((c) => (
          <Card key={c.id} data-testid={`company-${c.id}`} className="border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-md bg-blue-100 text-blue-600">
              <Building2 className="h-5 w-5" />
            </div>
            <div className="font-heading font-bold text-slate-900">{c.name}</div>
            <div className="mt-1 text-sm text-slate-500">{c.email || c.contact_email || "—"}</div>
            <div className="mt-1 text-sm text-slate-500">{[c.city, c.country].filter(Boolean).join(", ")}</div>
            <div className="overline mt-2 text-slate-400">{t("customers.zohoLabel")}: {c.zoho_customer_id || t("customers.notLinked")}</div>
            {c.supervisor_available && c.supervisor && (
              <div className="mt-3 flex items-center gap-1.5 rounded-md bg-emerald-50 px-2 py-1 text-xs text-emerald-700">
                <UserCheck className="h-3.5 w-3.5" /> {t("customers.supervisorTag")}: {c.supervisor.first_name} {c.supervisor.last_name}
              </div>
            )}
          </Card>
        ))}
      </div>

      <Dialog open={zohoOpen} onOpenChange={setZohoOpen}>
        <DialogContent className="border-slate-200 bg-white">
          <DialogHeader><DialogTitle className="font-heading text-slate-900">{t("customers.importTitle")}</DialogTitle><DialogDescription>{t("customers.importDesc")}</DialogDescription></DialogHeader>
          <p className="text-sm text-slate-500">{t("customers.importAvailNote")}</p>
          <div className="space-y-2">
            {zohoList.length === 0 && <p className="text-sm text-slate-400">{t("customers.noNewCustomers")}</p>}
            {zohoList.map((c) => (
              <div key={c.zoho_customer_id} data-testid={`zoho-cust-${c.zoho_customer_id}`} className="flex items-center justify-between rounded-md border border-slate-200 p-3">
                <div>
                  <div className="font-medium text-slate-900">{c.business_name}</div>
                  <div className="text-xs text-slate-500">{c.email} · {c.zoho_customer_id}</div>
                </div>
                <Button size="sm" data-testid={`import-${c.zoho_customer_id}`} onClick={() => importZoho(c.zoho_customer_id)} className="bg-blue-600 hover:bg-blue-500">{t("customers.import")}</Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
